import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON } from '@/lib/api'

type TicketAudienceValue = 'everyone' | 'members' | 'specific_levels'
type MembershipLevel = { id: number; name: string }

export function TicketAudience({
  audience,
  membershipLevelIds,
  onAudienceChange,
  onLevelsChange,
}: {
  audience: TicketAudienceValue
  membershipLevelIds: number[]
  onAudienceChange: (audience: TicketAudienceValue) => void
  onLevelsChange: React.Dispatch<React.SetStateAction<number[]>>
}) {
  const membershipLevels = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => getJSON<{ membership_levels: MembershipLevel[] }>('/api/v1/membership_levels'),
    enabled: audience === 'specific_levels',
  })

  return (
    <fieldset className="space-y-2">
      <Label htmlFor="ticket-type-audience" className="text-caption text-muted-foreground">
        Who can buy
      </Label>
      <Select
        value={audience}
        onValueChange={(value) => onAudienceChange(value as TicketAudienceValue)}
      >
        <SelectTrigger id="ticket-type-audience" className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="everyone">Everyone</SelectItem>
          <SelectItem value="members">Members — all levels</SelectItem>
          <SelectItem value="specific_levels">Specific Membership Levels</SelectItem>
        </SelectContent>
      </Select>
      {audience === 'specific_levels' && (
        <MembershipLevelChoices
          error={membershipLevels.isError}
          loading={membershipLevels.isLoading}
          levels={membershipLevels.data?.membership_levels}
          selectedIds={membershipLevelIds}
          onChange={onLevelsChange}
        />
      )}
    </fieldset>
  )
}

function MembershipLevelChoices({
  error,
  loading,
  levels,
  selectedIds,
  onChange,
}: {
  error: boolean
  loading: boolean
  levels?: MembershipLevel[]
  selectedIds: number[]
  onChange: React.Dispatch<React.SetStateAction<number[]>>
}) {
  return (
    <div className="space-y-1.5 rounded-md border border-border p-3">
      {loading ? (
        <p className="text-body-sm text-muted-foreground">Loading Membership Levels…</p>
      ) : error ? (
        <p role="alert" className="text-body-sm text-destructive">
          Membership Levels could not be loaded. Try again.
        </p>
      ) : levels?.length === 0 ? (
        <p className="text-body-sm text-muted-foreground">
          No Membership Levels are available. Create a Membership Level before restricting this
          Ticket Type.
        </p>
      ) : (
        levels?.map((level) => (
          <label key={level.id} className="flex items-center gap-2 text-body-sm">
            <Checkbox
              checked={selectedIds.includes(level.id)}
              onCheckedChange={(checked) =>
                onChange((ids) =>
                  checked ? [...ids, level.id] : ids.filter((id) => id !== level.id),
                )
              }
            />
            {level.name}
          </label>
        ))
      )}
    </div>
  )
}
